/** @file slot.renderMask: read-only; names a new image id and the mask spec the server rasterises (R6). */
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { CommandDef } from '../apply/commandDef';
import { restWorld } from '../node/restWorld';
import { subtreeOf } from '../node/subtreeOf';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { findSlot } from './findSlot';
import type { MaskSpec } from './maskSpec';

function volumeOf(p: Puppet, node: string): { radius: number; length: number } | undefined {
  const own = p.nodes[node]?.volume;
  if (own !== undefined) return { ...own };
  const child = Object.values(p.nodes).find((n) => n.parent === node);
  if (child === undefined) return undefined;
  const length = Math.hypot(...child.pos);
  return length > 0 ? { radius: 0.25 * length, length } : undefined;
}

function specOf(p: Puppet, slot: MaskSpec['slot'], style: MaskSpec['style']): MaskSpec {
  const node = p.drawables.find((d) => d.id === slot.drawable)?.node ?? '';
  const nodes = subtreeOf(p, node);
  const world: Record<string, Vec3> = Object.fromEntries(nodes.map((n) => [n, restWorld(p, n)]));
  const volumes = Object.fromEntries(nodes.flatMap((n) => { const v = volumeOf(p, n); return v === undefined ? [] : [[n, v]]; }));
  return { slot, style, size: [...slot.size], node, restWorld: world, volumes };
}

/** `slot.renderMask { slot, style? }` → `{ image, spec }` (style default `silhouette`); changes nothing. */
export const slotRenderMaskCommand: CommandDef<'slot.renderMask'> = {
  name: 'slot.renderMask',
  readOnly: true,
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['slot', 'style']);
    return compact({ slot: r.str('slot'), style: r.opt('style', (k) => r.oneOf(k, ['silhouette', 'skeleton'] as const)) });
  },
  /** Reads the draft and returns the image id and spec; the inverse is empty. */
  apply: (draft, input, ctx) => {
    const { slot } = findSlot(draft.puppet, input.slot);
    return { result: { image: ctx.makeId('img'), spec: specOf(draft.puppet, slot, input.style ?? 'silhouette') }, warnings: [], inverse: [] };
  },
};
